/**
 * Resolve `gs://` URIs to signed URLs an `<img>` can actually load.
 *
 * Needed only on the paths that read straight out of Firestore — the API's own
 * responses already carry signed URLs. Requests are batched per render and
 * memoised for the life of the page, because signing costs an IAM call and the
 * same crop appears on the progress screen and the results screen both.
 */
import { useEffect, useState } from 'react'

import { boot } from '../lib/boot'

const cache = new Map<string, string>()
let pending = new Set<string>()
let scheduled: Promise<void> | null = null
const waiters = new Set<() => void>()

async function flush(): Promise<void> {
  const uris = [...pending]
  pending = new Set()
  scheduled = null
  if (!uris.length) return

  const { auth } = await boot()
  const user = auth.currentUser
  if (!user) return

  try {
    const response = await fetch('/api/media/urls', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({ uris }),
    })
    if (response.ok) {
      const { urls } = (await response.json()) as { urls: Record<string, string> }
      for (const [uri, url] of Object.entries(urls)) cache.set(uri, url)
    }
  } catch (error) {
    console.warn('signing', error)
  }
  for (const notify of waiters) notify()
}

function want(uri: string): void {
  if (cache.has(uri) || pending.has(uri)) return
  pending.add(uri)
  // Coalesce every URI asked for during this render into one request.
  scheduled ??= Promise.resolve().then(flush)
}

export function useSignedUrls(uris: (string | null | undefined)[]): Record<string, string> {
  const [, bump] = useState(0)
  const wanted = uris.filter((uri): uri is string => Boolean(uri?.startsWith('gs://')))
  const key = wanted.join('|')

  useEffect(() => {
    const notify = () => bump((n) => n + 1)
    waiters.add(notify)
    for (const uri of wanted) want(uri)
    return () => {
      waiters.delete(notify)
    }
    // `key` is the identity of the set; `wanted` is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return Object.fromEntries(wanted.map((uri) => [uri, cache.get(uri) ?? '']))
}

/** Prefer an already-signed URL; fall back to resolving the `gs://` one. */
export function pick(
  signed: string | undefined,
  uri: string | null | undefined,
  resolved: Record<string, string>,
): string | undefined {
  return signed || (uri ? resolved[uri] || undefined : undefined)
}
